/**
 * Motion tokens for the Tenko design system. Use these instead of hard-coding
 * `duration: 0.3` everywhere — keeps animations consistent across the app and
 * makes a future audit (or a global slowdown for QA) a one-line change.
 *
 * Pair with framer-motion's `MotionConfig reducedMotion="user"` at the app
 * root so users with `prefers-reduced-motion` automatically opt out.
 */

/** Durations in seconds (framer-motion expects seconds, not ms). */
export const motionDuration = {
  /** Tooltip / micro feedback (~150ms). */
  fast: 0.15,
  /** Default for fades / slides / list reveals (~250ms). */
  base: 0.25,
  /** Larger surfaces — modals, drawers, page transitions (~350ms). */
  slow: 0.35,
} as const;

/** Easing curves. `easeOut` is the workhorse — settled feel at the end. */
export const motionEasing = {
  /** Default — content arriving / appearing. */
  out: [0.2, 0.8, 0.2, 1] as const,
  /** Content leaving / collapsing. */
  in: [0.4, 0, 1, 1] as const,
  /** In + out symmetric. Use sparingly. */
  inOut: [0.4, 0, 0.2, 1] as const,
} as const;

/** Stagger delay between siblings in a list reveal. */
export const motionStagger = {
  /** Tight — long lists, grid cards. */
  tight: 0.04,
  /** Standard — small lists, sidebars. */
  base: 0.08,
} as const;

/** Common transition presets for direct use in framer-motion `transition`. */
export const motionPresets = {
  fadeIn: {
    duration: motionDuration.base,
    ease: motionEasing.out,
  },
  fadeInFast: {
    duration: motionDuration.fast,
    ease: motionEasing.out,
  },
} as const;
